import { fetchResources } from '../api/galaxy.ts';
import { onApiMutation } from '../api/mutation-events.ts';
import { useGameStore } from '../store/game-store.ts';
import { refreshLiveResources, setSyncRequester } from './live-store.ts';

/** How often the server's own number is fetched while the tab is visible (the local ticker fills the gaps). */
export const RESOURCE_SYNC_INTERVAL_MS = 45_000;
/** A mutation (build, research, trade…) usually changes the stockpile: sync shortly after, batching bursts. */
const MUTATION_DEBOUNCE_MS = 250;

let inflight: Promise<void> | null = null;
let again = false;

/** Fetches the authoritative stockpile; concurrent calls share one request (one more follows if asked meanwhile). */
export function syncResources(): Promise<void> {
  if (inflight) { again = true; return inflight; }
  const t0 = Date.now();
  inflight = fetchResources()
    .then(res => { const t1 = Date.now(); useGameStore.getState().setResources(res, t1, t1 - t0); })
    .catch(() => undefined)
    .finally(() => {
      inflight = null;
      if (again) { again = false; void syncResources(); }
    });
  return inflight;
}

/**
 * Keeps the display honest without a page reload: periodic sync, and immediately when the tab becomes visible,
 * the window gets focus, the network returns, or any player action went through (`api/mutation-events`).
 */
export function installResourceSync(): () => void {
  let debounce: ReturnType<typeof setTimeout> | null = null;
  const soon = () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => { debounce = null; void syncResources(); }, MUTATION_DEBOUNCE_MS);
  };
  const onWake = () => { refreshLiveResources(); void syncResources(); };
  const onVisible = () => { if (document.visibilityState === 'visible') onWake(); };
  const interval = setInterval(() => { if (document.visibilityState !== 'hidden') void syncResources(); }, RESOURCE_SYNC_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onWake);
  window.addEventListener('online', onWake);
  window.addEventListener('pageshow', onWake);
  const offMutation = onApiMutation(soon);
  setSyncRequester(() => { void syncResources(); });
  return () => {
    clearInterval(interval);
    if (debounce) clearTimeout(debounce);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onWake);
    window.removeEventListener('online', onWake);
    window.removeEventListener('pageshow', onWake);
    offMutation();
    setSyncRequester(null);
  };
}
